import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultAddressBookGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";
import { mergeGridPrefs } from "~/shared/utils/merge-grid-prefs.ts";

const TRAILING_FIXED_FIELDS = ["default", "action"] as const;

type UseAddressBookGridColumnsArgs = {
	/** The code-defined Address Book columns from `useAddressBookColumns`. */
	columns: BoardColumn[];
	isInternal: boolean;
	/** The user's saved Address Book column prefs (external users only). */
	columnPrefs?: GridPrefColumn[];
};

/**
 * Builds the Address Book grid columns by merging the code-defined columns
 * with the user's persisted column preferences (visibility, order, width).
 *
 * Mirrors `useQuoteBoardColumns`. The trailing `default` and `action`
 * columns are pinned to the END of the grid and excluded from the reorderable
 * prefs pass, then re-appended afterward as non-reorderable columns.
 */
export const useAddressBookGridColumns = ({
	columns,
	isInternal,
	columnPrefs,
}: UseAddressBookGridColumnsArgs): BoardColumn[] => {
	return useMemo(() => {
		const prefsByField = new Map(
			(columnPrefs ?? []).map((column) => [column.columnId, column]),
		);
		const trailingColumns = TRAILING_FIXED_FIELDS.flatMap((field) =>
			columns.filter((col) => col.field === field),
		);
		const reorderableColumns = columns.filter(
			(col) =>
				!TRAILING_FIXED_FIELDS.includes(col.field as "default" | "action"),
		);

		const defaults =
			getDefaultAddressBookGridPrefs(isInternal)[
				GridIdentifiers["Address Book"]
			]?.columns ?? [];
		const mergedPrefs = mergeGridPrefs(defaults, columnPrefs ?? []);

		const applied = applyGridPrefs(reorderableColumns, mergedPrefs, isInternal);

		const trailing = trailingColumns.map((col, index) => {
			const field = typeof col.field === "string" ? col.field : "";
			const pref = prefsByField.get(field);

			return {
				...col,
				show: pref?.isVisible ?? col.show ?? true,
				width: typeof pref?.widthSize === "number" ? pref.widthSize : col.width,
				reorderable: false,
				resizable: field === "action" ? false : col.resizable,
				menu: field === "action" ? false : col.menu,
				orderIndex: applied.length + index,
			};
		});

		return [...applied, ...trailing];
	}, [columns, isInternal, columnPrefs]);
};
